import React, { useEffect, useState } from "react";
import styled from "styled-components";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import LineBtn from "../../components/Button/LineBtn";
import LineBox from "../../components/DefaultStyles/LineBox";
import PageTitle from "../../components/DefaultStyles/PageTitle";
import Table, { TableBody, TableBodyItem } from "../../components/Table/Table";
import { GetSubscriptionSetData } from "../../Api/ServiceDatas";
import Pagination from "../../components/Table/Pagination";
import { useApi } from "../../hooks/useApi";
import DotStatus from "../../components/DefaultStyles/DotStatus";
import PartyInfoFilter, {
  memberStateOption,
  partyStateOption,
  secretModeOption,
} from "../../components/pages/Party/PartyInfo/PartyInfoFilter";
import ExcelDownload from "../../components/DefaultStyles/ExcelDownload";
import Loader from "../../components/DefaultStyles/Loader";
import { atom, useRecoilState, useRecoilValue } from "recoil";
import { authType } from "../Login";
import SpaceBetween from "../../components/DefaultStyles/SpaceBetween";
import { TotalCount } from "../Users/UserInfo";
import { getThousandComma } from "../../Api/ApiCommon";
import Flex from "../../components/DefaultStyles/Flex";
import { OldMemberTag } from "../../components/pages/Party/PartyDetail/PartyDetailMemberInfo";
import { getPartyStatusText } from "../../AdminApi/commFunc";
import { getSubsNameBySubsId3 } from "../../AdminApi/3.0_utils";

interface IPartyInfo {
  partyid: string;
  servsubsid: number;
  state: number;
  startdate: string;
  enddate: string;
  maxmemb: number;
  members: number;
  invitememb: number;
  isSecret: number;
  ownerid: string;
  exitdate: string | null;
  childpid: number;
  sset_value: number;
  mystate: number;
}

export const partyInfoFilterState = atom({
  key: "partyInfoFilterState",
  default: {
    searchValue: "",
    subsid: {
      value: "",
      label: "",
    },
    state: {
      value: 100,
      label: "",
    },
    isFull: {
      value: 10,
      label: "",
    },
    isSecret: {
      value: 10,
      label: "",
    },
    page: -1,
  },
});

export const partyInfoPageState = atom<number>({
  key: "partyInfoPageState",
  default: 0,
});

export default function PartyInfo() {
  const navigate = useNavigate();
  const auth = useRecoilValue(authType);
  const [partyInfoFilter, setPartyInfoFilter] =
    useRecoilState(partyInfoFilterState);

  const [savedPage, setSavedPage] = useRecoilState<number>(partyInfoPageState);
  const [page, setPage] = useState(savedPage);
  const [searchValue, setSearchValue] = useState<string | undefined>(
    partyInfoFilter?.searchValue !== "" ? partyInfoFilter?.searchValue : ""
  );

  const [filteringValue, setFilteringValue] = useState({
    isClicked: false,
    servsubsid: partyInfoFilter?.subsid?.value ?? "",
    state: partyInfoFilter?.state?.value ?? 100,
    isFull: partyInfoFilter?.isFull?.value ?? 10,
    isSecret: partyInfoFilter?.isSecret?.value ?? 10,
  });

  const isFullLabel = memberStateOption?.find(
    (val) => val.value === `${filteringValue?.isFull}`
  )?.label;

  const isSecretLabel = secretModeOption?.find(
    (val) => val.value === `${filteringValue?.isSecret}`
  )?.label;

  const stateLabel = partyStateOption?.find(
    (val) => val.value === `${filteringValue?.state}`
  )?.label;

  const { response, loading } = useApi({
    method: "get",
    url:
      `party?page=${page}&size=10` +
      (searchValue !== "" ? `&partyid=${searchValue}` : "") +
      (filteringValue?.servsubsid !== ""
        ? `&servsubsid=${filteringValue?.servsubsid}`
        : "") +
      (filteringValue?.isFull !== 10
        ? `&isfull=${filteringValue?.isFull}`
        : ``) +
      (filteringValue?.isSecret !== 10
        ? `&isSecret=${filteringValue?.isSecret}`
        : ``) +
      (filteringValue?.state !== 100 ? `&state=${filteringValue?.state}` : ``),
  });

  const tableHead = [
    "PID",
    "Subscription",
    "파티장 UID",
    "상태",
    "시작일",
    "종료일",
    "모집인원",
    "시크릿",
    "상세",
  ];

  useEffect(() => {}, [filteringValue]);

  useEffect(() => {
    setSavedPage(page);
  }, [page]);

  return (
    <>
      <PageTitle title="파티 정보" />

      <PartyInfoFilter
        setSearchValue={setSearchValue}
        filteringValue={filteringValue}
        setFilteringValue={setFilteringValue}
        setPage={setPage}
      />

      <LineBox>
        <Table tableHeadData={tableHead} tableMinWidth={879}>
          <>
            {response?.data?.items?.map((d: IPartyInfo) => {
              const isInfinity = d?.enddate === "9999-12-31";

              return (
                <TableBody key={d?.partyid}>
                  <TableBodyItem>{d?.partyid}</TableBodyItem>
                  <TableBodyItem>
                    {d?.servsubsid}_{getSubsNameBySubsId3(d?.servsubsid)}
                  </TableBodyItem>
                  <TableBodyItem
                    clickable={true}
                    onClick={() => navigate(`/users/info/${d?.ownerid}`)}
                  >
                    {d?.ownerid}
                  </TableBodyItem>
                  <TableBodyItem>
                    <DotStatus
                      text={getPartyStatusText(d?.mystate, d.enddate).text}
                      color={getPartyStatusText(d?.mystate, d.enddate).color}
                      size="sm"
                    />
                  </TableBodyItem>
                  <TableBodyItem>
                    <Flex gapSize={0}>
                      <>{dayjs(d?.startdate).format("YYYY-MM-DD")}</>

                      <>
                        {d?.childpid > 0 && (
                          <OldMemberTag>
                            {d?.sset_value < 0 ? "일출" : "연장"}
                          </OldMemberTag>
                        )}
                      </>
                    </Flex>
                  </TableBodyItem>
                  <TableBodyItem
                    style={{
                      textDecoration:
                        d?.exitdate === "" && d?.state === 3
                          ? "line-through"
                          : "none",
                    }}
                  >
                    {isInfinity ? (
                      d?.state !== 0 && d?.state !== 3 ? (
                        "-"
                      ) : (
                        dayjs(d?.exitdate ? d?.exitdate : d?.enddate)
                          .add(-1, "day")
                          .format("YYYY-MM-DD")
                      )
                    ) : (
                      <>
                        {d?.exitdate && (
                          <ExitDate>
                            {dayjs(d?.enddate)
                              .add(-1, "day")
                              .format("YYYY-MM-DD")}
                          </ExitDate>
                        )}

                        {dayjs(d?.exitdate ? d?.exitdate : d?.enddate)
                          .add(-1, "day")
                          .format("YYYY-MM-DD")}
                      </>
                    )}
                  </TableBodyItem>
                  <TableBodyItem>
                    {d?.members}/{d?.maxmemb}{" "}
                    {d?.invitememb > 0 ? "(" + d?.invitememb + ")" : ""}
                  </TableBodyItem>
                  <TableBodyItem
                    style={{ color: d?.isSecret ? "#00c7c7" : "#2f2f38" }}
                  >
                    {d?.isSecret ? "ON" : "OFF"}
                  </TableBodyItem>

                  <TableBodyItem>
                    <LineBtn
                      btnText="상세"
                      btnClickEvent={() => {
                        setPartyInfoFilter({
                          searchValue: searchValue ?? "",
                          subsid: {
                            value: filteringValue?.servsubsid ?? "",
                            label:
                              filteringValue?.servsubsid !== ""
                                ? `${
                                    filteringValue?.servsubsid
                                  }_${getSubsNameBySubsId3(
                                    +filteringValue?.servsubsid
                                  )}`
                                : "",
                          },
                          isFull: {
                            value:
                              filteringValue?.isFull !== 10
                                ? filteringValue?.isFull
                                : 10,
                            label:
                              filteringValue?.isFull !== 10
                                ? `${isFullLabel}`
                                : "",
                          },
                          isSecret: {
                            value:
                              filteringValue?.isSecret !== 10
                                ? filteringValue?.isSecret
                                : 10,
                            label:
                              filteringValue?.isSecret !== 10
                                ? `${isSecretLabel}`
                                : "",
                          },
                          state: {
                            value:
                              filteringValue?.state !== 100
                                ? filteringValue?.state
                                : 100,
                            label:
                              filteringValue?.state !== 100
                                ? `${stateLabel}`
                                : "",
                          },
                          page,
                        });

                        navigate(`/party/info/${d?.partyid}`);
                      }}
                      disabled={!auth?.partyInfo}
                    />
                  </TableBodyItem>
                </TableBody>
              );
            })}
          </>
        </Table>

        <SpaceBetween>
          <Pagination
            totalPage={response?.status === 200 ? response.data.totalPage : 1}
            currentPage={page}
            pageClickEvent={(index) => setPage(index)}
          />

          <TotalCount>
            총{" "}
            {getThousandComma(
              response?.status === 200 ? +response?.data?.totalCount : 0
            )}
            개
          </TotalCount>
        </SpaceBetween>
      </LineBox>

      <ExcelDownload
        disabled={response?.data?.items?.length < 1}
        tableHead={tableHead}
        pushedData={(d: any) => {
          return [
            d.partyid,
            `${d?.servsubsid}_
                    ${getSubsNameBySubsId3(d?.servsubsid)}`,
            d?.ownerid,
            d?.state === 0
              ? "종료"
              : d?.state === 1
              ? "진행중"
              : d?.state === 2
              ? "해산 처리중"
              : d?.state === 3
              ? "해산"
              : d?.state === 4
              ? "예정"
              : "",
            dayjs(d?.startdate).format("YYYY-MM-DD"),
            dayjs(d?.enddate).format("YYYY-MM-DD"),
            `${d?.members}/${d?.maxmemb}`,
            d?.isSecret ? "ON" : "OFF",
          ];
        }}
        dataUrl={
          `party?page=0&size=1000` +
          (searchValue !== "" ? `&partyid=${searchValue}` : "") +
          (filteringValue?.servsubsid !== ""
            ? `&servsubsid=${filteringValue?.servsubsid}`
            : "") +
          (filteringValue?.isFull !== 10
            ? `&isfull=${filteringValue?.isFull}`
            : ``) +
          (filteringValue?.isSecret !== 10
            ? `&isSecret=${filteringValue?.isSecret}`
            : ``) +
          (filteringValue?.state !== 100
            ? `&state=${filteringValue?.state}`
            : ``)
        }
        title={"파티 정보"}
      />

      {loading && <Loader />}
    </>
  );
}

const ExitDate = styled.div`
  text-decoration: line-through;
`;
